import EVImg from '@components/EVImg/EVImg';
import { Block } from '@components/Layout';
import { ContentContainer } from '@components/Layout/ContentContainer';
import { Close } from '@icon/index';
import { generateListTracEvent } from '@util/ListTrac';
import { FC, useState } from 'react';
import { A11y } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';
import GalleryStreetView from './GalleryStreetView';
import GalleryVideo from './GalleryVideo';
import GalleryVirtualTour from './GalleryVirtualTour';
import { ModalWrapper } from './styles';
import {
  GalleryModalCloseButton,
  GalleryModalContainer,
  GalleryModalContent,
  GalleryModalHeader,
  GalleryModalImageWrapper,
  GalleryModalImages,
  GalleryModalNavButton,
  GalleryModalNavSwiper,
} from './styles-modal';
import { MediaGalleryData } from './mapData';

interface GalleryImagesAllModalProps
  extends Omit<MediaGalleryData, 'listingStatus' | 'cooperativeAgencyStatus'> {
  id: string;
  isStreetViewAvailable: boolean;
  setSelectedButton: (button: string) => void;
  setFloorPlanModalSwiper: (swiper: any) => void;
}

const GalleryImagesAllModal: FC<GalleryImagesAllModalProps> = ({
  id,
  imagesArr,
  floorPlanArr,
  virtualTourData,
  streetViewData,
  isStreetViewAvailable,
  videoData,
  setSelectedButton,
  setFloorPlanModalSwiper,
  listingId,
  zipCode,
}) => {
  const [selectedModalButton, setSelectedModalButton] = useState('All Images'); // state to store the selected button

  const renderSelectedModalComponent = () => {
    switch (selectedModalButton) {
      case 'All Images':
        return (
          <GalleryModalImages>
            {imagesArr &&
              imagesArr.map((image, index) => {
                return (
                  <GalleryModalImageWrapper key={index}>
                    <EVImg className="image-modal" src={image} alt="Listing Image" sizes="300px" />
                  </GalleryModalImageWrapper>
                );
              })}
          </GalleryModalImages>
        );
      case 'Floorplan':
        return (
          <GalleryModalImages>
            {floorPlanArr &&
              floorPlanArr.map((image, index) => {
                return (
                  <GalleryModalImageWrapper key={index}>
                    <EVImg className="image-modal" src={image} alt="Listing Image" sizes="300px" />
                  </GalleryModalImageWrapper>
                );
              })}
          </GalleryModalImages>
        );
      case 'Virtual Tour':
        return (
          <ModalWrapper style={{ marginBottom: '4rem' }}>
            {virtualTourData && (
              <>
                {virtualTourData!.includes('yout') ? (
                  <GalleryVideo videoData={virtualTourData} />
                ) : (
                  <GalleryVirtualTour virtualTourData={virtualTourData} />
                )}
              </>
            )}
          </ModalWrapper>
        );
      case 'Street View':
        return (
          <ModalWrapper style={{ marginBottom: '4rem' }}>
            <GalleryStreetView
              streetViewData={streetViewData!}
              isStreetViewAvailable={isStreetViewAvailable}
            />
          </ModalWrapper>
        );
      case 'Video':
        return (
          <ModalWrapper style={{ marginBottom: '4rem' }}>
            <GalleryVideo videoData={videoData!} />
          </ModalWrapper>
        );
      default:
        return null;
    }
  };

  const handleCloseModal = () => {
    const modal = document.getElementById(id);
    if (!modal) {
      return;
    }

    modal.classList.remove('modal-open');
    document.body.classList.remove('media-gallery-modal-open'); //need body tag to hide scrollbar, this style is in the global styles sheet (@components/Layout/GlobalStyles)
    //set setSelectedModalButton to "Images" to reset the modal
    setSelectedModalButton('All Images');
    setSelectedButton('Images');
  };

  //Buttons for the modal
  const modalButtons = [
    {
      name: 'All Images',
      condition: imagesArr && imagesArr.length > 0 ? imagesArr : null,
    },
    {
      name: 'Floorplan',
      condition: floorPlanArr && floorPlanArr.length > 0 ? floorPlanArr : null,
    },
    { name: 'Virtual Tour', condition: virtualTourData },
    { name: 'Street View', condition: streetViewData && isStreetViewAvailable },
    { name: 'Video', condition: videoData },
  ];

  return (
    <GalleryModalContainer id={id}>
      <GalleryModalHeader>
        <GalleryModalNavSwiper // adding swiper for if/when buttons go beyond the width of the modal
          modules={[A11y]}
          spaceBetween={0}
          slidesPerView={'auto'}
        >
          {modalButtons.map((button, index) => {
            //render buttons based on the condition
            if (button.condition) {
              return (
                <SwiperSlide key={index}>
                  <GalleryModalNavButton
                    key={button.name}
                    type="button"
                    $active={selectedModalButton === button.name}
                    onClick={async () => {
                      setSelectedModalButton(button.name);
                      if (button.name === 'All Images' || button.name === 'Virtual Tour') {
                        const eventType = button.name === 'All Images' ? 'gallery' : 'vTour';
                        await generateListTracEvent({
                          type: eventType,
                          listingId,
                          zipCode,
                        });
                      }
                    }}
                  >
                    {button.name}
                  </GalleryModalNavButton>
                </SwiperSlide>
              );
            }
            return null;
          })}
        </GalleryModalNavSwiper>

        <GalleryModalCloseButton onClick={handleCloseModal} type="button">
          <Close />
          <span className="visually-hidden">Close</span> {/* screen reader text */}
        </GalleryModalCloseButton>
      </GalleryModalHeader>

      <GalleryModalContent>
        <ContentContainer>
          <Block>{renderSelectedModalComponent()}</Block>
        </ContentContainer>
      </GalleryModalContent>
    </GalleryModalContainer>
  );
};

export default GalleryImagesAllModal;
